import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { PolicyData, MedicalRequirement, UnderwritingRequirement } from "../../api/ebam/policy/policy-response";
import { onChangeParticipationRequirement, onChangePolicyInformation, onChangeUnderwritingProvision, setAllowedNextStatus, setEffectiveDate } from "../../features/ebam/plicy-data-page/policy-information/policy-information.slice";
import { onChangePolicyCoveragePremiumDetails } from "../../features/ebam/plicy-data-page/policy-coverage-premium-details/policy-coverage-premium-details.slice";
import { onChangeSpecialUnderWritingProvisionsState } from "../../features/ebam/plicy-data-page/policy-special-underwriting-provisions/policy-special-underwriting-provisions.slice";
import { policyType } from "../../features/ebam/plicy-data-page/policy-data-page.slice";
import { DueDatesInfo } from "../../features/ebam/plicy-data-page/policy-coverage-premium-details/policy-coverage-premium-details.state";
import { policyDataPageType } from "../../constant/ebam-constant";

interface Props {
    policy?: PolicyData;
    policyType: policyType;
}

export const usePopulatePolicy = ({ policy, policyType }: Props) => {
    
    const dispatch = useDispatch();
    const policyTypeGCLIP = policyType === policyDataPageType.gcli_prin || policyType === policyDataPageType.gcli_out;

    const populateSigningAddressAndDocumentCode = useCallback(() => {
        if (!policy) return;

        dispatch(onChangePolicyInformation({ field: "docCode", value: policy.doc_code ?? "" }));
        dispatch(onChangePolicyInformation({ field: "cityMunicipalityProvince", value: policy.signing_location ?? "" }));
    }, [dispatch, policy]);

    const populatePolicyInformation = useCallback(() => {
        if (!policy) return;

        dispatch(setAllowedNextStatus(policy.allowed_next_statuses ?? []));
        dispatch(setEffectiveDate({
            isEdit: false,
            value: policy.effective_date?.split('T')[0] ?? ''
        }));

        if (policyTypeGCLIP) {
            if (policy.gclip_eligible_individuals) {
                dispatch(onChangePolicyInformation({ field: "eligibleIndividuals", value: policy.gclip_eligible_individuals.eligible_individuals }));
            }
            if (policy.gclip_contribution) {
                dispatch(onChangePolicyInformation({ field: "contribution", value: policy.gclip_contribution.contribution }));
            }
            if (policy.gclip_participation_requirements) {
                const participationPercentage = String(policy.gclip_participation_requirements.participation_percentage ?? "").replace(/%$/, "");
                dispatch(onChangeParticipationRequirement({ id: 1, amount: participationPercentage }));
                dispatch(onChangeParticipationRequirement({ id: 2, amount: policy.gclip_participation_requirements.participation_minimum_no }));
            }
            if (policy.gclip_underwriting_notes) {
                const nel: UnderwritingRequirement = policy.gclip_underwriting_notes.nel?.[0];
                const nmed: UnderwritingRequirement = policy.gclip_underwriting_notes.nmed?.[0];
                const med: MedicalRequirement = policy.gclip_underwriting_notes.med?.[0];
                const maxLimit: UnderwritingRequirement = policy.gclip_underwriting_notes.max_limit?.[0];

                if (nel) {
                    dispatch(onChangeUnderwritingProvision({ id: 1, field: "amount", value: Number(nel.amount.replace(/,/g, "")) }));
                    dispatch(onChangeUnderwritingProvision({ id: 1, field: "ageRange", value: nel.age }));
                }
                if (nmed) {
                    dispatch(onChangeUnderwritingProvision({ id: 2, field: "amount", value: Number(nmed.amount.replace(/,/g, "")) }));
                    dispatch(onChangeUnderwritingProvision({ id: 2, field: "ageRange", value: nmed.age }));
                }
                if (med) {
                    dispatch(onChangeUnderwritingProvision({ id: 3, field: "description", value: med.amount }));
                }
                if (maxLimit) {
                    dispatch(onChangeUnderwritingProvision({ id: 4, field: "amount", value: Number(maxLimit.amount.replace(/,/g, "")) }));
                    dispatch(onChangeUnderwritingProvision({ id: 4, field: "ageRange", value: maxLimit.age }));
                }
                dispatch(onChangePolicyInformation({ field: "underwritingProvisionsNotes", value: policy.gclip_underwriting_notes.underwriting_notes }));
            }
        }

        if (policyType === policyDataPageType.gpa_gaddp) {
            if (policy.gpa_eligible_individuals) {
                dispatch(onChangePolicyInformation({ field: "eligibleIndividuals", value: policy.gpa_eligible_individuals.eligible_individuals }));
            }
            if (policy.gpa_contribution) {
                dispatch(onChangePolicyInformation({ field: "contribution", value: policy.gpa_contribution.contribution }));
            }
            if (policy.gpa_participation_requirements) {
                const participationPercentage = String(policy.gpa_participation_requirements.participation_percentage ?? "").replace(/%$/, "");
                dispatch(onChangeParticipationRequirement({ id: 1, amount: participationPercentage }));
                dispatch(onChangeParticipationRequirement({ id: 2, amount: policy.gpa_participation_requirements.participation_minimum_no }));
            }
            if (policy.gpa_special_provisions) {
                dispatch(onChangePolicyInformation({ field: "specialUnderwritingProvisions", value: policy.gpa_special_provisions.special_provisions }));
            }
        }
    }, [dispatch, policy, policyType, policyTypeGCLIP]);

    const populateScheduleOfInsurance = useCallback(() => {
        if (!policy) return;
        if (policyTypeGCLIP && policy.gclip_schedule_of_insurance) {
            dispatch(onChangePolicyCoveragePremiumDetails({ field: "amountOfInsurance", value: policy.gclip_schedule_of_insurance.amount_of_insurance }));
            dispatch(onChangePolicyCoveragePremiumDetails({ field: "coveragePeriod", value: policy.gclip_schedule_of_insurance.coverage_period }));
            dispatch(onChangePolicyCoveragePremiumDetails({ field: "dueDates", value: policy.gclip_schedule_of_insurance.due_dates }));
            dispatch(onChangePolicyCoveragePremiumDetails({ field: "refundOfPremiums", value: policy.gclip_schedule_of_insurance.refund_of_premiums }));
            dispatch(onChangePolicyCoveragePremiumDetails({ field: "terminationAge", value: Number(policy.gclip_schedule_of_insurance.termination_age) }));
        }
        if (policyType === policyDataPageType.gpa_gaddp && policy.gpa_schedule_of_insurance) {
            const dueDatesInfo: DueDatesInfo = {
                first_due_date: policy.gpa_schedule_of_insurance.first_due_date ?? "",
                renewal_due_date: policy.gpa_schedule_of_insurance.renewal_due_date ?? "",
                additions_due_date: policy.gpa_schedule_of_insurance.additions_due_date ?? "",
            };
            dispatch(onChangePolicyCoveragePremiumDetails({ field: "dueDatesInfo", value: dueDatesInfo }));
        }
    }, [dispatch, policy, policyType, policyTypeGCLIP]);

    const populateSpecialUnderwritingProvisions = useCallback(() => {
        if (!policy) return;

        if (policyTypeGCLIP && policy.gclip_special_provision) {
            dispatch(onChangeSpecialUnderWritingProvisionsState({ field: "enrollment", value: policy.gclip_special_provision.enrollment ?? "" }));
            dispatch(onChangeSpecialUnderWritingProvisionsState({ field: "rollOverProvisions", value: policy.gclip_special_provision.rollover_provision ?? "" }));
            dispatch(onChangeSpecialUnderWritingProvisionsState({ field: "terminationOfIndividual", value: policy.gclip_special_provision.termination_of_insurance ?? "" }));
            dispatch(onChangeSpecialUnderWritingProvisionsState({ field: "generalDefinitions", value: policy.gclip_special_provision.general_definitions ?? "" }));
            dispatch(onChangeSpecialUnderWritingProvisionsState({ field: "faceAmountOfInsurance", value: policy.gclip_special_provision.face_amount_insurance ?? "" }));
            dispatch(onChangeSpecialUnderWritingProvisionsState({ field: "premiumComputation", value: policy.gclip_special_provision.premium_computation ?? "" }));
            dispatch(onChangeSpecialUnderWritingProvisionsState({ field: "nonCoverageProvisions", value: policy.gclip_special_provision.non_coverage_provision ?? "" }));
            dispatch(onChangeSpecialUnderWritingProvisionsState({ field: "claimsProcedure", value: policy.gclip_special_provision.claims_procedure ?? "" }));
        }
        if (policyType === policyDataPageType.gpa_gaddp && policy.gpa_special_provisions) {
            dispatch(onChangeSpecialUnderWritingProvisionsState({ field: "specialProvisions", value: policy.gpa_special_provisions.special_provisions ?? "" }));
        }
    }, [dispatch, policy, policyType, policyTypeGCLIP]);

    return {
        populatePolicyInformation,
        populateScheduleOfInsurance,
        populateSpecialUnderwritingProvisions,
        populateSigningAddressAndDocumentCode,
    };
};